import { useEffect, useRef, useState } from "react";
import { HudCard } from "./HudCard";
import { Markdown } from "./Markdown";
import { BuilderResult } from "./BuilderResult";
import { Loader2, AlertTriangle, Download, Maximize2, X, Copy, Check } from "lucide-react";
import { copyToClipboard } from "../lib/clipboard";
import { toast } from "sonner";

export function TranscriptFeed({ transcript, interim, listening }) {
  const endRef = useRef(null);
  const [lightbox, setLightbox] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const dlUrl = (u) => `${BACKEND}/api/image/download?url=${encodeURIComponent(u)}`;

  const handleCopy = async (id, text) => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopiedId(id);
      toast.success("Copié !");
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1800);
    } else {
      toast.error("Copie impossible sur cet appareil.");
    }
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcript, interim]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <>
    <HudCard label="JOURNAL DES TRANSMISSIONS" className="flex flex-col h-full">
      <div data-testid="transcript-feed-container" className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
        {transcript.length === 0 && !interim && (
          <p className="font-mono-hud text-[11px] text-[#556] tracking-wide leading-relaxed">
            » Aucune transmission pour l'instant. Parlez ou écrivez pour commencer la conversation.
          </p>
        )}
        {transcript.map((m, i) => {
          const isUser = m.role === "user";
          return (
            <div
              key={m.id || i}
              data-testid={isUser ? "transcript-item-user" : "transcript-item-assistant"}
              className="animate-rise"
            >
              <div
                className={`font-mono-hud text-[9px] tracking-[0.25em] uppercase mb-1 ${
                  isUser ? "text-[#8b949e]" : "accent-text"
                }`}
              >
                {isUser ? "OPÉRATEUR" : "SARA CONNOR"}
              </div>
              <div
                className={`text-sm leading-relaxed px-3 py-2 clip-corner ${
                  isUser
                    ? "bg-[rgba(255,255,255,0.04)] text-[#c8d6e5] border-l-2 border-[#8b949e]"
                    : "bg-[var(--accent-a10)] text-[#eaf4ff] border-l-2 border-[var(--accent)]"
                }`}
              >
                {isUser ? m.text : <Markdown>{m.text}</Markdown>}
              </div>
              {!isUser && m.text && (
                <div className="mt-1 flex justify-end">
                  <button
                    type="button"
                    data-testid={`copy-message-${m.id || i}`}
                    onClick={() => handleCopy(m.id || i, m.text)}
                    className="inline-flex items-center gap-1 px-2 py-1 clip-corner border border-[var(--border-hud)] font-mono-hud text-[9px] tracking-[0.15em] uppercase text-[#8b949e] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors"
                    title="Copier la réponse complète"
                  >
                    {copiedId === (m.id || i)
                      ? (<><Check className="h-3 w-3" /> Copié !</>)
                      : (<><Copy className="h-3 w-3" /> Copier</>)}
                  </button>
                </div>
              )}
              {m.building && (
                <div
                  data-testid="builder-generating"
                  className="mt-2 flex items-center gap-2 clip-corner border border-[var(--border-hud)] bg-[var(--accent-a10)] px-3 py-2 font-mono-hud text-[11px] tracking-wide text-[#8fb8d8]"
                >
                  <Loader2 className="h-3.5 w-3.5 animate-spin accent-text" />
                  Sara écrit le code du projet… (Claude Sonnet 5)
                </div>
              )}
              {m.buildError && (
                <div className="mt-2 flex items-center gap-2 clip-corner border border-[#ff6b6b]/40 bg-[#ff6b6b]/10 px-3 py-2 font-mono-hud text-[11px] tracking-wide text-[#ffb3b3]">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Échec de la génération. Reformulez la demande et réessayez.
                </div>
              )}
              {m.build && (
                <div className="mt-2">
                  <BuilderResult project={m.build} compact />
                </div>
              )}
              {m.images?.length > 0 && (
                <div className="mt-2 grid gap-2" data-testid="transcript-images">
                  {m.images.map((img, idx) => (
                    <div
                      key={idx}
                      className="relative group clip-corner overflow-hidden border border-[var(--border-hud)]"
                      style={{ boxShadow: "0 0 18px var(--accent-a25)" }}
                      data-testid="transcript-image"
                    >
                      <button
                        type="button"
                        onClick={() => setLightbox(img)}
                        className="block w-full"
                        title="Agrandir en plein écran"
                        data-testid="image-zoom-button"
                      >
                        <img
                          src={img.url}
                          alt={img.prompt || "Image générée par Sara"}
                          className="w-full h-auto max-h-80 object-contain bg-black cursor-zoom-in transition-transform duration-300 group-hover:scale-[1.02]"
                          loading="lazy"
                        />
                      </button>
                      <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => setLightbox(img)}
                          className="p-1.5 clip-corner bg-black/60 backdrop-blur border border-[var(--border-hud)] text-[#c8d6e5] hover:text-white hover:bg-black/80"
                          title="Plein écran"
                          data-testid="image-fullscreen-button"
                        >
                          <Maximize2 className="h-3.5 w-3.5" />
                        </button>
                        <a
                          href={dlUrl(img.url)}
                          className="p-1.5 clip-corner bg-black/60 backdrop-blur border border-[var(--border-hud)] accent-text hover:text-white hover:bg-black/80"
                          title="Télécharger l'image"
                          data-testid="image-download-button"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {interim && listening && (
          <div className="opacity-60">
            <div className="font-mono-hud text-[9px] tracking-[0.25em] uppercase mb-1 text-[#8b949e]">OPÉRATEUR</div>
            <div className="text-sm leading-relaxed px-3 py-2 italic text-[#8b949e]">{interim}…</div>
          </div>
        )}
        <div ref={endRef} />
      </div>
    </HudCard>

    {lightbox && (
      <div
        className="fixed inset-0 z-[120] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
        onClick={() => setLightbox(null)}
        data-testid="image-lightbox"
      >
        <div className="relative max-w-5xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
          <img
            src={lightbox.url}
            alt={lightbox.prompt || "Image générée par Sara"}
            className="max-w-full max-h-[80vh] object-contain clip-corner border border-[var(--accent-a25)]"
            style={{ boxShadow: "0 0 44px var(--accent-a25)" }}
          />
          {lightbox.prompt && (
            <p className="mt-3 text-center font-mono-hud text-[11px] leading-relaxed text-[#8fb8d8] max-w-2xl">
              {lightbox.prompt}
            </p>
          )}
          <div className="absolute top-2 right-2 flex gap-2">
            <a
              href={dlUrl(lightbox.url)}
              className="p-2 clip-corner bg-black/70 backdrop-blur border border-[var(--border-hud)] accent-text hover:text-white hover:bg-black/90"
              title="Télécharger l'image"
              data-testid="lightbox-download-button"
            >
              <Download className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="p-2 clip-corner bg-black/70 backdrop-blur border border-[var(--border-hud)] text-[#c8d6e5] hover:text-white hover:bg-black/90"
              title="Fermer (Échap)"
              data-testid="lightbox-close-button"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
